import { useState } from 'react';

import { RunStatus } from '../../../../../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Run } from '@/api/types/backend-dto-types';
import { Person } from '@/api/types/backend-dto-types';
import { Label } from '@/components/atoms/label';
import MultiSelect from '@/components/atoms/MultiSelect';
import { DateRangeFilter } from '@/components/molecules/DateRangeFilter';
import { DocumentFilter } from '@/components/molecules/DocumentFilter';
import { DocumentTypeFilter } from '@/components/molecules/DocumentTypeFilter';
import FilterPopup from '@/components/molecules/FilterPopup';
import { transformFilterName } from '@/helpers/filterNames';
import type { FactFilters as FactFiltersType, MyOptionType } from '@/types';
import { Relevance, Classification } from '@/types';

const FactFiltersPopup = ({
  allDocumentTypes,
  allPersons,
  activeFilters,
  setFilters,
  handleClose,
  caseRuns,
  caseThemes,
  goToPage,
}: {
  allDocumentTypes: string[];
  allPersons: Person[];
  activeFilters: FactFiltersType;
  setFilters: (v: FactFiltersType) => void;
  handleClose: () => void;
  caseRuns: Run[];
  caseThemes: string[];
  goToPage?: (value: number) => void;
}) => {
  const currentIncludedDocs = activeFilters.docIds || [];
  const currentExcludedDocs = activeFilters.excludedDocIds || [];
  const currentDocumentTypes = activeFilters.documentTypes || [];
  const currentPersonIds = (activeFilters.personIds || []).filter((person) => person !== null);

  // Use maps for included/excluded docs for better performance
  const [includedDocs, setIncludedDocs] = useState<Map<string, MyOptionType>>(
    new Map(currentIncludedDocs.filter((doc) => doc !== null).map((doc) => [doc.value, doc])),
  );
  const [excludedDocs, setExcludedDocs] = useState<Map<string, MyOptionType>>(
    new Map(currentExcludedDocs.filter((doc) => doc !== null).map((doc) => [doc.value, doc])),
  );
  const [documentTypeFilters, setDocumentTypeFilters] = useState<MyOptionType[]>(
    currentDocumentTypes?.map(type => ({ 
      label: type === '' ? 'Unassigned' : type, 
      value: type 
    }))?.filter((type) => type !== null) || [],
  );
  const [personFilters, setPersonFilters] = useState<MyOptionType[]>(
    currentPersonIds?.map(personId => {
      const person = allPersons.find(p => p.id === personId);
      return { label: person?.name || personId, value: personId };
    }) || []
  );

  const [relevanceFilters, setRelevanceFilters] = useState<MyOptionType[]>(
    activeFilters.relevance?.map(rel => ({ label: rel, value: rel })) || []
  );

  const [classificationFilters, setClassificationFilters] = useState<MyOptionType[]>(
    activeFilters.classification?.map(cls => ({ label: cls, value: cls })) || []
  );
  const [runIdFilters, setRunIdFilters] = useState<MyOptionType[]>(activeFilters.runIds || []);
  const [startDate, setStartDate] = useState<Date | null>(
    activeFilters.startDate ? new Date(activeFilters.startDate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    activeFilters.endDate ? new Date(activeFilters.endDate) : null
  );
  const [themeFilters, setThemeFilters] = useState<string[]>(activeFilters.themes || []);

  const handleSubmit = () => {
    const newFilters = { ...activeFilters };
    newFilters.docIds = Array.from(includedDocs.values());
    newFilters.excludedDocIds = Array.from(excludedDocs.values());
    newFilters.documentTypes = documentTypeFilters.map(type => type?.value).filter((v): v is string => Boolean(v));
    newFilters.relevance = relevanceFilters.map(rel => rel?.value).filter((v): v is Relevance => Boolean(v)) as Relevance[];
    newFilters.classification = classificationFilters.map(cls => cls?.value).filter((v): v is Classification => Boolean(v)) as Classification[];
    newFilters.runIds = runIdFilters;
    newFilters.startDate = startDate?.toISOString() || null;
    newFilters.endDate = endDate?.toISOString() || null;
    newFilters.themes = themeFilters;
    newFilters.personIds = personFilters.map(person => person?.value).filter((v): v is string => Boolean(v));

    setFilters(newFilters);
    handleClose();
  };

  const handleClear = () => {
    setFilters({
      classification: [],
      docIds: [],
      excludedDocIds: [],
      relevance: [],
      runIds: [],
      keyFacts: Boolean(activeFilters.keyFacts),
      disputedFacts: Boolean(activeFilters.disputedFacts),
      startDate: null,
      endDate: null,
      documentTypes: [],
      themes: [],
      personIds: [],
    });
    handleClose();
  };

  return (
    <FilterPopup
      title="Filter Facts"
      description="Narrow down your facts search"
      onApply={handleSubmit}
      onClear={handleClear}
      goToPage={goToPage}
    >
      {/* Documents Filter */}
      <div>
        <Label className="mb-2 block">Documents</Label>
        <DocumentFilter
          includedDocs={includedDocs}
          excludedDocs={excludedDocs}
          setIncludedDocs={(docs) => {
            trackCustomEvent(TrackingEvents.FilterFactsByDocument);
            setIncludedDocs(docs);
          }}
          setExcludedDocs={(docs) => {
            trackCustomEvent(TrackingEvents.FilterFactsByDocument);
            setExcludedDocs(docs);
          }}
          mode="include-facts-from"
        />
      </div>

      {/* Document Type Filter */}
      <div>
        <Label className="mb-2 block">Document Type</Label>
        <DocumentTypeFilter
          selectedTypes={documentTypeFilters}
          setSelectedTypes={(types) => {
            trackCustomEvent(TrackingEvents.FilterFactsByDocumentType);
            setDocumentTypeFilters(types);
          }}
          allDocumentTypes={allDocumentTypes}
        />
      </div>

      {/* Themes Filter */}
      <div>
        <Label className="mb-2 block">Themes</Label>
        <MultiSelect
          placeholder="All themes"
          options={caseThemes.map((theme) => ({
            label: theme,
            value: theme,
          }))}
          selectedOptions={themeFilters.map((theme) => ({ label: theme, value: theme }))}
          onChange={(newValue) => {
            trackCustomEvent(TrackingEvents.FilterFactsByTheme);
            setThemeFilters(newValue.filter((option) => option !== null).map((option) => option.value));
          }}
          className="w-full"
        />
      </div>

      {/* Relevance and Classification in horizontal layout */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block">Relevance</Label>
          <MultiSelect
            placeholder="All relevance levels"
            options={Object.values(Relevance).map((relevance) => ({
              label: transformFilterName(relevance, 'factRelevance'),
              value: relevance,
            }))}
            selectedOptions={relevanceFilters}
            onChange={(newValue) => {
              trackCustomEvent(TrackingEvents.FilterFactsByRelevance);
              setRelevanceFilters([...newValue]);
            }}
            className="w-full"
          />
        </div>
        <div>
          <Label className="mb-2 block">Classification</Label>
          <MultiSelect
            placeholder="All classifications"
            options={Object.values(Classification).map((classification) => ({
              label: transformFilterName(classification),
              value: classification,
            }))}
            selectedOptions={classificationFilters}
            onChange={(newValue) => {
              trackCustomEvent(TrackingEvents.FilterFactsByClassification);
              setClassificationFilters([...newValue]);
            }}
            className="w-full"
          />
        </div>
      </div>

      {/* Processing Run and Entities in horizontal layout */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block">Processing Run</Label>
          <MultiSelect
            placeholder="All processing runs"
            options={caseRuns
              .filter((run, index, array) => {
                // Include the first run OR runs that added new documents
                const isFirstRun = index === array.length - 1;
                return isFirstRun || (run.isUpdateNewDocuments && run.status === RunStatus.Complete);
              })
              .map((run) => ({
                label: new Date(run.createdDate).toLocaleString(),
                value: run.id,
              }))}
            selectedOptions={runIdFilters}
            onChange={(newValue) => {
              trackCustomEvent(TrackingEvents.FilterFactsByProcessingRun);
              setRunIdFilters([...newValue]);
            }}
            className="w-full"
          />
        </div>
        <div>
          <Label className="mb-2 block">Entities</Label>
          <MultiSelect
            placeholder="All entities"
            options={allPersons.map((person) => ({
              label: person.name,
              value: person.id,
            }))}
            selectedOptions={personFilters}
            onChange={(newValue) => {
              trackCustomEvent(TrackingEvents.FilterFactsByPerson);
              setPersonFilters([...newValue]);
            }}
            className="w-full"
          />
        </div>
      </div>

      {/* Fact Date Range Filter */}
      <div>
        <Label className="mb-2 block">Fact Date Range</Label>
        <DateRangeFilter startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      </div>
    </FilterPopup>
  );
};

export default FactFiltersPopup;
